import {
  DateFilterType,
  Filter,
  FilterKind,
  NumberFilterType,
  TextFilterType
} from '@app/shared/grid/datasource-utils';

/**
 * Equality filter: field == value.
 * @param field Target field path.
 * @param value Value to match.
 * @return A filter model item.
 */
export function eq(field: string, value: any): Filter {
  return {kind: FilterKind.EQUALS, field, value};
}

/**
 * Equality filter: field != value.
 * @param field Target field path.
 * @param value Value to not match.
 * @return A filter model item.
 */
export function nEq(field: string, value: any): Filter {
  return {kind: FilterKind.NOT_EQUALS, field, value};
}

/**
 * Set filter: field value must be one of the given values.
 * @param field Target field path.
 * @param values Allowed values.
 * @return A filter model item.
 */
export function inSet(field: string, values: any[]): Filter {
  return {kind: FilterKind.IN, field, values};
}

/**
 * Existence filter.
 * @param field Target field path.
 * @param present A boolean that represents the existence of the field at the given path.
 * @return A filter model item.
 */
export function exists(field: string, present: boolean): Filter {
  return {kind: FilterKind.EXISTS, field, value: present};
}

/**
 * OR group. At least one condition must be satisfied.
 * @param conditions List of [Filter] conditions, at least one have to be true.
 * @return A filter model item.
 */
export function orGroup(...conditions: Filter[]): Filter {
  return {kind: FilterKind.OR, conditions};
}

/**
 * $elemMatch on an array field.
 * @param field Array field path.
 * @param conditions Conditions applied to array elements (ANDed).
 * @return A filter model item.
 */
export function elemMatch(field: string, ...conditions: Filter[]): Filter {
  return {kind: FilterKind.ELEM_MATCH, field, conditions};
}

/**
 * Helper: field contains substring.
 * @param field Target field path.
 * @param value Substring to search.
 * @return A filter model item.
 */
export function textContains(field: string, value?: string): Filter {
  return {kind: FilterKind.TEXT, field, textFilter: {type: TextFilterType.TEXT_CONTAINS, filter: value}};
}

/**
 * Helper: field equals string.
 * @param field Target field path.
 * @param value Exact value.
 * @return A filter model item.
 */
export function textEquals(field: string, value: string): Filter {
  return {kind: FilterKind.TEXT, field, textFilter: {type: TextFilterType.TEXT_EQUALS, filter: value}};

}

/**
 * Helper: field not equals string.
 * @param field Target field path.
 * @param value Value to match.
 * @return A filter model item.
 */
export function textNotEquals(field: string, value: any): Filter {
  return {kind: FilterKind.TEXT, field, textFilter: {type: TextFilterType.TEXT_NOT_EQUALS, filter: value}};
}

/**
 * Helper: field starts with prefix.
 * @param field Target field path.
 * @param value Prefix.
 * @return A filter model item.
 */
export function textStartsWith(field: string, value: string): Filter {
  return {kind: FilterKind.TEXT, field, textFilter: {type: TextFilterType.TEXT_STARTS_WITH, filter: value}};
}

/**
 * Helper: field ends with suffix.
 * @param field Target field path.
 * @param value Suffix.
 * @return A filter model item.
 */
export function textEndsWith(field: string, value: string): Filter {
  return {kind: FilterKind.TEXT, field, textFilter: {type: TextFilterType.TEXT_ENDS_WITH, filter: value}};
}

/**
 * Helper: field does not contain substring.
 * @param field Target field path.
 * @param value Substring to exclude.
 * @return A filter model item.
 */
export function textNotContains(field: string, value: string): Filter {
  return {kind: FilterKind.TEXT, field, textFilter: {type: TextFilterType.TEXT_NOT_CONTAINS, filter: value}};
}

/**
 * Helper: from <= field <= to.
 * @param field Target field path.
 * @param from Lower bound (inclusive).
 * @param to Upper bound (inclusive).
 * @return A filter model item.
 */
export function numberBetween(field: string, from: number, to: number): Filter {
  return {
      kind: FilterKind.NUMBER,
      field,
      numberFilter: {
        type: NumberFilterType.NUMBER_BETWEEN,
        filter: from,
        filterTo: to
      }
    };
}

/**
 * Helper: field >= value.
 * @param field Target field path.
 * @param value Threshold.
 * @return A filter model item.
 */
export function numberGte(field: string, value: number): Filter {
  return {
    kind: FilterKind.NUMBER,
    field,
    numberFilter: {
      type: NumberFilterType.NUMBER_GREATER_THAN_OR_EQUAL,
      filter: value
    }
  };
}

/**
 * Helper: field <= value.
 * @param field Target field path.
 * @param value Threshold.
 * @return A filter model item.
 */
export function numberLte(field: string, value: number): Filter {
  return {
    kind: FilterKind.NUMBER,
    field,
    numberFilter: {
      type: NumberFilterType.NUMBER_LESS_THAN_OR_EQUAL,
      filter: value
    }
  };
}

/**
 * Helper: field in [from, to].
 * @param field Target field path.
 * @param from ISO date lower bound (inclusive).
 * @param to ISO date upper bound (inclusive).
 * @return A filter model item.
 */
export function dateInRange(field: string, from: string, to: string): Filter {
  return {
      kind: FilterKind.DATE,
      field,
      dateFilter: {
        type: DateFilterType.DATE_BETWEEN,
        dateFrom: from,
        dateTo: to
      }
    };
}

/**
 * Helper: field < date.
 * @param field Target field path.
 * @param date ISO date.
 * @return A filter model item.
 */
export function dateBefore(field: string, date: string): Filter {
  return {
    kind: FilterKind.DATE,
    field,
    dateFilter: {
      type: DateFilterType.DATE_BEFORE,
      dateFrom: date
    }
  };
}

/**
 * Helper: field > date.
 * @param field Target field path.
 * @param date ISO date.
 * @return A filter model item.
 */
export function dateAfter(field: string, date: string): Filter {
  return {
    kind: FilterKind.DATE,
    field,
    dateFilter: {
      type: DateFilterType.DATE_AFTER,
      dateFrom: date
    }
  };
}

/**
 * Top-level AND composition. Filters out undefined items.
 * @param items Items to AND together (top-level AND is implicit).
 * @return A list of filter items.
 */
export function allOf(...items: Array<Filter | undefined>): Filter[] {
  return items.filter(Boolean) as Filter[];
}

/**
 * Top-level OR composition. Wraps items in a single OR group. Filters out undefined items.
 * @param items Items to OR together.
 * @return A list containing a single OR group, or empty if no items.
 */
export function anyOf(...items: Array<Filter | undefined>): Filter[] {
  const filtered = items.filter(Boolean) as Filter[];
  return filtered.length ? [orGroup(...filtered)] : [];
}

/**
 * Helper: OR(exists(field)=false, field IN values).
 * @param field Target field path.
 * @param values Allowed values for the field.
 * @return A list containing one OR group.
 */
export function missingOrInSet(field: string, values: any[]): Filter[] {
  return anyOf(exists(field, false), inSet(field, values));
}
